import type { ReactNode } from "react";

// The hexagon logo mark (clip-path polygon). Renders a tenant logo image when branding provides
// one, otherwise a glyph (default 🐝) inside the gold hexagon. Also the avatar shape in dense views.
export function HexLogo({ glyph = "🐝", logoUrl, size = 36 }: { glyph?: string; logoUrl?: string; size?: number }) {
  return (
    <div className="hex" style={{ width: size, height: size, fontSize: size * 0.47 }} aria-hidden="true">
      {logoUrl ? (
        <img src={logoUrl} alt="" style={{ width: "70%", height: "70%", objectFit: "contain" }} />
      ) : (
        <span>{glyph}</span>
      )}
    </div>
  );
}

// A status dot that is NEVER colour-only: it renders the coloured dot AND the text label together.
export function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span
        aria-hidden="true"
        style={{ width: 7, height: 7, borderRadius: 99, background: color, flex: "none" }}
      />
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.2 }}>{label}</span>
    </span>
  );
}

export function Pill({ className, children }: { className: string; children: ReactNode }) {
  return <span className={`pill ${className}`}>{children}</span>;
}

export function Tile({ children, className = "", gold = false }: { children: ReactNode; className?: string; gold?: boolean }) {
  return <div className={`glass-tile ${gold ? "gold" : ""} ${className}`} style={{ padding: 18 }}>{children}</div>;
}

export function SectionHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 2 }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px" }}>{title}</div>
      {children}
    </div>
  );
}

// Avatar initials in a rounded brown mini-tile (dense list/board views).
export function Avatar({ name, kind = "mini" }: { name: string; kind?: "mini" | "lg" }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  if (kind === "lg") {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          background: "var(--gradient-accent)",
          display: "grid",
          placeItems: "center",
          fontSize: 11.5,
          fontWeight: 700,
          color: "#fff",
        }}
        aria-hidden="true"
      >
        {initials}
      </div>
    );
  }
  return (
    <span
      style={{
        width: 21,
        height: 21,
        borderRadius: 6,
        background: "var(--brown)",
        display: "grid",
        placeItems: "center",
        fontSize: 9,
        fontWeight: 700,
        color: "#FFE9C9",
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
